import * as Promise6 from "es6-promise";

import { IFloodgateAuthTokenCallback } from "@ms-ofb/officefloodgatecore/dist/src/Api/IFloodgateAuthTokenCallback";
import { MessageSpec } from "@ms-ofb/officefloodgatecore/dist/src/Governance/MessageMetadata";

import { TmsTransport } from "./tmstransport";
import { ContentType } from "./tmsContentType";
import { buildQueryParameters, IQueryParams } from "../Common/UrlUtils";
import { TmsCache, CacheItem } from "./tmscache";

import * as Constants from "../Constants";
import * as Logging from "../Logging/Logging";
import * as TmsConstants from "./tmsConstants";
import * as Utils from "../Utils";

import { getDynamicSetting, SettingKey } from "../Configuration/DynamicConfiguration";
import FloodgateStorageProvider from "../FloodgateCore/FloodgateStorageProvider";
import { TMS_SERVICE_ACTION_URL } from "./tmsConstants";
import { IBeaconParameters } from "../FloodgateCore/CoachingUXLauncherFactory";

export class ContentResponse {
	public contentType: ContentType;
	public content: any;
}

interface ITmsServiceResponse {
	MessageMetadata: MessageSpec;
	UserGovernanceRules: any;
	CampaignContent: any;
	DynamicSettings: any;
	LogLevelSettings: ILogLevelSettings;
}

interface ILogLevelSettings {
	logLevel: Logging.LogLevel;
}

export class TmsClient {
	private locale: string = "en-us";
	private country: string = "US";
	private authTokenCallback: IFloodgateAuthTokenCallback;
	private app: string;
	private platform: string;
	private version: string;
	private sessionId: string;
	private campaignFlights: string;
	private campaignParameters: string;

	private isInit: boolean = false;
	private tmsCache: TmsCache;
	private tmsTransport: TmsTransport;

	public async initialize(
		app: string,
		platform: string,
		version: string,
		locale: string,
		country: string,
		sessionId: string,
		authTokenCallback: IFloodgateAuthTokenCallback,
		campaignFlights?: string,
		campaignParameters?: string,
		tmsCacheIn?: TmsCache): Promise6.Promise<boolean> {
		if (this.isInit) {
			return this.isInit;
		}

		this.locale = locale;
		this.country = country;
		this.authTokenCallback = authTokenCallback;
		this.app = app;
		this.platform = platform;
		this.version = version;
		this.sessionId = sessionId;
		this.campaignFlights = campaignFlights;
		this.campaignParameters = campaignParameters;

		this.tmsCache = this.tmsCache || tmsCacheIn || new TmsCache([
			ContentType.messageMetadata,
			ContentType.userGovernanceRules,
			ContentType.campaignContent,
			ContentType.dynamicSettings,
			ContentType.logLevelSettings,
		]);

		await this.refreshItemsInCacheFromService([
			ContentType.messageMetadata,
			ContentType.userGovernanceRules,
			ContentType.campaignContent,
			ContentType.dynamicSettings,
			ContentType.logLevelSettings]);

		this.isInit = true;

		return this.isInit;
	}

	public async getContent(
		requestedContent: ContentType,
		app: string,
		platform: string,
		version: string,
		locale: string,
		country: string,
		sessionId: string,
		authTokenCallback: IFloodgateAuthTokenCallback,
		campaignFlights?: string,
		campaignParameters?: string): Promise6.Promise<ContentResponse> {
		if (!this.isInit) {
			await this.initialize(
				app,
				platform,
				version,
				locale,
				country,
				sessionId,
				authTokenCallback,
				campaignFlights,
				campaignParameters);
		}

		const response: ContentResponse = this.retrieveCacheItemAndCreateContentResponse(requestedContent);
		return response;
	}

	public async getMultipleContents(
		requestedContent: Array<ContentType>,
		app: string,
		platform: string,
		version: string,
		locale: string,
		country: string,
		sessionId: string,
		authTokenCallback: IFloodgateAuthTokenCallback,
		campaignFlights?: string,
		campaignParameters?: string): Promise6.Promise<ContentResponse[]> {
		if (!this.isInit) {
			await this.initialize(
				app,
				platform,
				version,
				locale,
				country,
				sessionId,
				authTokenCallback,
				campaignFlights,
				campaignParameters);
		}

		const contentResponses: ContentResponse[] = new Array<ContentResponse>();
		const bulkRefreshList: ContentType[] = new Array<ContentType>();
		for (let contentType of requestedContent) {
			const cacheItem: CacheItem = this.tmsCache.getItem(contentType);
			const responseItem: ContentResponse = this.createContentResponse(contentType, cacheItem);

			contentResponses.push(responseItem);
		}

		this.refreshItemsInCacheFromService(bulkRefreshList);

		return contentResponses;
	}

	public async sendBeaconRequestToService(
		app: string,
		beaconParameters: IBeaconParameters,
		platform: string,
		version: string,
		locale: string,
		country: string,
		sessionId: string,
		authTokenCallback: IFloodgateAuthTokenCallback
		): Promise6.Promise<void> {
		this.sessionId = sessionId;
		this.authTokenCallback = authTokenCallback;
		const authToken = await this.getAuthToken(Constants.GUIDs.TMSAppID);
		let puid: string = undefined;
		if (!authToken) {
			puid = this.authTokenCallback.getUserPuid && await this.authTokenCallback.getUserPuid();
		}

		try {
			const tmsQueryParams: IQueryParams = {
				app,
				...beaconParameters,
				country,
				locale,
				platform,
				puid,
				version,

			};
			const response: Response = await this.sendRequest(
				tmsQueryParams,
				authToken,
				SettingKey.tmsActionUrl,
				TMS_SERVICE_ACTION_URL);
			if (!response || response.status !== 200) {
				Logging.getLogger().logEvent(
					Logging.EventIds.WebSurfaces.Common.Error.VALUE,
					Logging.LogLevel.Error,
					this.createTelemetryData(response, "Beacon"));
			}
		} catch (error) {
			Logging.getLogger().logEvent(
				Logging.EventIds.WebSurfaces.Common.Error.VALUE,
				Logging.LogLevel.Error,
				{
					ErrorMessage:
						"Error fetching from TMS SetCampaignAction endpoint" + (error && error.message ? ": " + error.message : ""),
					...this.createTelemetryData(null, "Beacon"),
				});
		}
	}

	private createTelemetryData(response: Response, type: string): Logging.ICustomProperties {
		const requestStartTime = +new Date();
		const getRequestDuration = () => +new Date() - requestStartTime;
		const { headers, status, statusText } = response || {} as Response;
		const serverSessionId = headers && headers.get("X-UserSessionId");
		const serverCorrelationId = headers && headers.get("X-CorrelationId");
		return {
			Type: type,
			TimeMilliseconds: getRequestDuration(),
			SessionId: serverSessionId || this.sessionId,
			CorrelationId: serverCorrelationId || undefined,
			HttpStatusCode: status || undefined,
			HttpStatusText: statusText || undefined,
		};
	}

	private async sendRequest(tmsQueryParams: IQueryParams, authToken: string, key: SettingKey, serviceUrl: string) {
		this.tmsTransport = this.tmsTransport || new TmsTransport(this.sessionId);
		const queryParamStr = buildQueryParameters(tmsQueryParams);
		const tmsBaseUrl = getDynamicSetting(key, serviceUrl);
		const response: Response = await this.tmsTransport.sendGetRequest(tmsBaseUrl + queryParamStr, authToken);
		return response;
	}

	private retrieveCacheItemAndCreateContentResponse(contentType: ContentType): ContentResponse {
		const cacheItem: CacheItem = this.tmsCache.getItem(contentType);
		const responseItem: ContentResponse = this.createContentResponse(contentType, cacheItem);
		if (Utils.isNullorUndefined(cacheItem) || cacheItem.expiryDate.getMilliseconds() > Date.now()) {
			this.refreshItemsInCacheFromService([contentType]);
		}
		return responseItem;
	}

	private createContentResponse(requestedContent: ContentType, cacheItem: CacheItem): ContentResponse {
		const contentResponse: ContentResponse = new ContentResponse();
		contentResponse.content = cacheItem !== undefined ? cacheItem.content : null;

		contentResponse.contentType = requestedContent;

		return contentResponse;
	}

	private createCacheItem(content: any, contentType: ContentType, expiryTime: number): CacheItem {
		let cacheItem: CacheItem = new CacheItem(contentType);
		cacheItem.content = content;
		cacheItem.lastRetrievedDate = new Date();
		cacheItem.expiryDate = new Date(Date.now() + expiryTime);

		return cacheItem;
	}

	private async getAuthToken(appIdOrUri: string): Promise6.Promise<string> {
		if (!this.authTokenCallback || !this.authTokenCallback.getAuthToken) {
			return null;
		}

		const startTime = +new Date();
		const getDuration = () => +new Date() - startTime;
		const createAuthTelemetryData = (errorMessage: string = undefined): Logging.ICustomProperties => {
			return {
				ErrorMessage: errorMessage,
				SessionId: this.sessionId,
				TimeMilliseconds: getDuration(),
				Type: appIdOrUri,
			};
		};

		try {
			const retVal = await this.authTokenCallback.getAuthToken(appIdOrUri);
			Logging.getLogger().logEvent(
				Logging.EventIds.WebSurfaces.Common.Info.VALUE,
				Logging.LogLevel.Info,
				createAuthTelemetryData());

			return retVal;
		} catch (error) {
			Logging.getLogger().logEvent(
				Logging.EventIds.WebSurfaces.Common.Error.VALUE,
				Logging.LogLevel.Error,
				createAuthTelemetryData("Error getting token " + (error && error.message ? ": " + error.message : "")));
		}

		return null;
	}

	private async refreshItemsInCacheFromService(contentTypes: Array<ContentType>): Promise6.Promise<void> {
		if (contentTypes.length === 0) {
			return;
		}

		// check if touched items are actually due for refresh or are still valid
		const bulkRefreshList: ContentType[] = new Array<ContentType>();
		for (let contentType of contentTypes) {
			const cacheItem: CacheItem = this.tmsCache.getItem(contentType);
			if (Utils.isNullorUndefined(cacheItem)
				|| !cacheItem.expiryDate
				|| cacheItem.expiryDate.getMilliseconds() > Date.now()) {
				bulkRefreshList.push(contentType);
			}
		}

		if (bulkRefreshList.length === 0) {
			// exit out if all items in cache are still valid to avoid unnecessary service roundtrips 
			return;
		}

		const authToken = await this.getAuthToken(Constants.GUIDs.TMSAppID);
		let userPuid: string = undefined;
		if (!authToken) {
			userPuid = this.authTokenCallback.getUserPuid && await this.authTokenCallback.getUserPuid();
		}

		// make service call for expired or uncached items
		try {
			// capture browser data to perform additional filters on IRIS
			// this is to avoid showing bad UI experience on unsupported browsers/dimensions
			const documentElement = document && (document.documentElement || document.getElementsByTagName("body")[0]);
			const browserParams: IQueryParams = {
				hasStorage: FloodgateStorageProvider.isStorageAvailable(),
				pageWidth: window.innerWidth || documentElement.clientWidth,
				pageHeight: window.innerHeight || documentElement.clientHeight,
				screenWidth: screen ? screen.width : undefined,
				screenHeight: screen ? screen.height : undefined,
				colorDepth: screen ? screen.colorDepth : undefined,
			};

			const tmsQueryParams: IQueryParams = {
				country: this.country,
				locale: this.locale,
				app: this.app,
				platform: this.platform,
				version: this.version,
				campaignParams: buildQueryParameters(browserParams) + "&more=true&" + (this.campaignParameters || ""),
				contentType: contentTypes.join(";"),
				puid: userPuid,
				OFC_FLIGHTS: this.campaignFlights,
			};

			const response: Response = await this.sendRequest(
				tmsQueryParams,
				authToken,
				SettingKey.tmsServiceUrl,
				TmsConstants.TMS_SERVICEURL);
			if (response.status === 200) {
				const tmsServiceResponse: ITmsServiceResponse = await response.json();

				if (contentTypes.indexOf(ContentType.messageMetadata) !== -1
					&& tmsServiceResponse.MessageMetadata !== undefined) {
					this.tmsCache.setItem(
						this.createCacheItem(
							tmsServiceResponse.MessageMetadata,
							ContentType.messageMetadata,
							TmsConstants.CACHE_EXPIRY_MESSAGEMETADATA));
				}

				if (contentTypes.indexOf(ContentType.userGovernanceRules) !== -1
					&& tmsServiceResponse.UserGovernanceRules !== undefined) {
					this.tmsCache.setItem(
						this.createCacheItem(
							tmsServiceResponse.UserGovernanceRules,
							ContentType.userGovernanceRules,
							TmsConstants.CACHE_EXPIRY_USERGOVERNANCERULES));
				}

				if (contentTypes.indexOf(ContentType.campaignContent) !== -1
					&& tmsServiceResponse.CampaignContent !== undefined) {
					this.tmsCache.setItem(
						this.createCacheItem(
							tmsServiceResponse.CampaignContent,
							ContentType.campaignContent,
							TmsConstants.CACHE_EXPIRY_CAMPAIGNCONTENT));
				}

				if (contentTypes.indexOf(ContentType.dynamicSettings) !== -1
					&& tmsServiceResponse.DynamicSettings !== undefined) {
					this.tmsCache.setItem(
						this.createCacheItem(
							tmsServiceResponse.DynamicSettings,
							ContentType.dynamicSettings,
							TmsConstants.CACHE_EXPIRY_DYNAMICSETTINGS));
				}

				if (contentTypes.indexOf(ContentType.logLevelSettings) !== -1
					&& tmsServiceResponse.LogLevelSettings !== undefined) {
					this.tmsCache.setItem(
						this.createCacheItem(
							tmsServiceResponse.LogLevelSettings,
							ContentType.logLevelSettings,
							TmsConstants.CACHE_EXPIRY_LOGLEVELSETTINGS));
				}

				Logging.getLogger().logEvent(
					Logging.EventIds.WebSurfaces.Messaging.Requests.VALUE,
					Logging.LogLevel.Info,
					this.createTelemetryData(response, contentTypes.join(";")));
			} else {
				Logging.getLogger().logEvent(
					Logging.EventIds.WebSurfaces.Common.Error.VALUE,
					Logging.LogLevel.Error,
					this.createTelemetryData(response, contentTypes.join(";")));
			}
		} catch (error) {
			Logging.getLogger().logEvent(
				Logging.EventIds.WebSurfaces.Common.Error.VALUE,
				Logging.LogLevel.Error,
				{
					ErrorMessage: "Error fetching from TMS" + (error && error.message ? ": " + error.message : ""),
					...this.createTelemetryData(null, contentTypes.join(";")),
				});
		}
	}
}
